import _ from 'lodash'
import React, {useState, useEffect} from 'react'
import {Link, useLocation} from 'react-router-dom'
import {getAccess, getRole} from '../../helpers/ReduxHelpers'
import {useSidebarToggle} from '../../common/SidebarToggleContext'
import {useKycCounts} from '../../common/KycCountsContext'
import MENU_ITEMS from './menuConfig'
import {buildMenuSearchIndex, searchMenu} from './menuSearch'
import {EXPANDED, COLLAPSED} from './sidebarNewStyles'
import {
  Box,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  Divider,
} from '@mui/material'
import {ExpandLess, ExpandMore} from '@mui/icons-material'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight'
import SearchIcon from '@mui/icons-material/Search'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess'
import ClearIcon from '@mui/icons-material/Clear'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import AirportShuttleOutlinedIcon from '@mui/icons-material/AirportShuttleOutlined'
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined'
import PriceChangeOutlinedIcon from '@mui/icons-material/PriceChangeOutlined'
import {InputAdornment, TextField} from '@mui/material'

// ── Section icons — map section names to visual icons ──
const SECTION_ICONS = {
  'D2C FREIGHT': <LocalShippingOutlinedIcon sx={{fontSize: 14}} />,
  'PTL FREIGHT': <AirportShuttleOutlinedIcon sx={{fontSize: 14}} />,
  'D2C PRICING AGENT': <PriceChangeOutlinedIcon sx={{fontSize: 14}} />,
  'ADMIN': <AdminPanelSettingsOutlinedIcon sx={{fontSize: 14}} />,
}

// ── RBAC persona pills (planning artifact — backend role wiring pending) ──
// Persona shortcodes shown next to gated menu items:
//   ONB = MERCHANT_ONBOARDING  (Onboarding Specialist · KYC upload, Pricing, TAT setup + updates)
//   APR = MERCHANT_KYC_APPROVAL (KYC Approver · Finance team · KYC review & sign-off)
//   ALC = MERCHANT_ALLOCATION  (Allocation Manager · D2C Allocation setup + updates)
const RBAC_PERSONA_BG = {
  ONB: 'rgba(32,101,209,0.20)',
  APR: 'rgba(245,158,11,0.20)',
  ALC: 'rgba(168,85,247,0.20)',
}
const RBAC_PERSONA_FG = {
  ONB: '#60a5fa',
  APR: '#fbbf24',
  ALC: '#c084fc',
}
const RBAC_PERSONA_TOOLTIP = {
  ONB: 'Role: MERCHANT_ONBOARDING · Onboarding Specialist (backend wiring pending)',
  APR: 'Role: MERCHANT_KYC_APPROVAL · KYC Approver / Finance (backend wiring pending)',
  ALC: 'Role: MERCHANT_ALLOCATION · Allocation Manager (backend wiring pending)',
}

const SidebarNew = () => {
  const loc = useLocation()
  const [openSubMenu, setOpenSubMenu] = useState('')
  const [openNestedSubMenu, setOpenNestedSubMenu] = useState('')
  const [selectedMenu, setSelectedMenu] = useState(null)
  const [searchText, setSearchText] = useState('')
  const [allExpanded, setAllExpanded] = useState(false)
  const {sidebarWidth, handleMouseEnter, setSidebarWidth, isHovered, handleMouseLeave} =
    useSidebarToggle()
  const kycCounts = useKycCounts()

  const isExpanded = !sidebarWidth || isHovered
  const drawerWidth = isExpanded ? EXPANDED : COLLAPSED

  // ── Search ──
  // Runs over a flat index of every navigable leaf (menuSearch.js) and renders a result
  // list. Pruning the tree in place buried the hit two levels down, kept every sibling of a
  // level-3 match, and gave no way to tell apart the many repeated names ("Dashboard",
  // "Zone Mapping", "Product Master", "All Shipments" …).
  const query = searchText.trim()
  const searchIndex = React.useMemo(() => buildMenuSearchIndex(), [])
  const searchMatches = React.useMemo(
    () => (query ? searchMenu(searchIndex, query) : []),
    [query, searchIndex]
  )

  // Marks the matched words inside `text`; returns it untouched when there's no active query
  // or no match, so it's safe to call unconditionally.
  // Matching is per-word rather than one contiguous span: the query is tokenised and order
  // no longer matters, so a single indexOf('ndr open') would highlight nothing in "Open NDR".
  const highlightMatch = (text) => {
    if (!query || !text) return text
    const tokens = query.toLowerCase().split(/\s+/).filter(Boolean)
    if (tokens.length === 0) return text
    const parts = String(text).split(
      new RegExp(`(${tokens.map((t) => _.escapeRegExp(t)).join('|')})`, 'ig')
    )
    if (parts.length === 1) return text
    return (
      <>
        {parts.map((part, i) =>
          tokens.includes(part.toLowerCase()) ? (
            <Box component='span' key={i} sx={sx.highlight}>
              {part}
            </Box>
          ) : (
            part
          )
        )}
      </>
    )
  }

  /* Active matching: path-boundary prefix + longest-match-wins, so sibling routes that
     share a prefix (e.g. /supply/partners vs /supply/partners/onboard) never both light up.
     For submenu items with ?tab= params, match pathname + search together. */
  const matchesPath = (pathname, url) => !!url && (pathname === url || pathname.startsWith(url + '/'))
  const fullLoc = loc.pathname + loc.search
  let bestMatchUrl = ''
  {
    const visit = (items) =>
      (items || []).forEach((it) => {
        if (!it || it.type) return
        if (it.url) {
          const hasQuery = it.url.includes('?')
          if (hasQuery) {
            // Exact match including query params (e.g. /dashboard/d2c?tab=1)
            if (fullLoc === it.url && it.url.length > bestMatchUrl.length) bestMatchUrl = it.url
          } else {
            if (matchesPath(loc.pathname, it.url) && it.url.length > bestMatchUrl.length) bestMatchUrl = it.url
          }
        }
        if (it.submenu) visit(it.submenu)
      })
    visit(MENU_ITEMS)
  }
  const isItemActive = (url) => !!url && url === bestMatchUrl

  const handleSubMenuClick = (menuName) => {
    setOpenSubMenu(openSubMenu === menuName ? '' : menuName)
  }

  const handleNestedSubMenuClick = (submenuUrl) => {
    setOpenNestedSubMenu(openNestedSubMenu === submenuUrl ? '' : submenuUrl)
  }

  useEffect(() => {
    const full = loc.pathname + loc.search
    const menuItems = MENU_ITEMS.filter((m) => !m.type)
    const found = menuItems.find(
      (menu) =>
        loc.pathname === menu.url ||
        (menu.submenu &&
          menu.submenu.some(
            (sub) =>
              !sub.type &&
              (loc.pathname === sub.url || full === sub.url ||
                (sub.submenu && sub.submenu.some((n) => loc.pathname === n.url || full === n.url)))
          ))
    )
    setSelectedMenu(found)
  }, [loc])

  // The search field only renders while expanded. Drop the query with it, or the collapsed
  // rail stays silently filtered with no visible input to clear.
  useEffect(() => {
    if (!isExpanded) setSearchText('')
  }, [isExpanded])

  // Module flags (accessList) plus JWT roles (e.g. ROLE_PROSHIPADMIN) — the
  // latter is for items gated on a backend isProshipAdmin() check rather than
  // a granted module; existing role arrays only ever list module flags, so
  // this merge is additive and doesn't change any other item's visibility.
  const userAccess = [...getAccess(), ...getRole()]

  // ── RBAC visibility (applies at every nesting level) ──
  //   A leaf is visible when its role intersects the user's access (or it has no role).
  //   A group (item with a submenu) is visible only when at least one descendant is visible,
  //   so a user who is gated out of every child never sees an empty group shell.
  const roleOk = (roleArr) => !roleArr || _.intersection(roleArr, userAccess).length > 0
  const isItemVisible = (it) => {
    if (!it || it.type) return false // dividers / section headers are not selectable items
    if (!roleOk(it.role)) return false
    if (it.submenu && it.submenu.length) return it.submenu.some((c) => isItemVisible(c))
    return true
  }

  // A search result is visible only if every level down to it passes — the tree gets this
  // implicitly (a nested item renders only because its parent did), so the flat index has
  // to re-apply each ancestor's gate or search would reach past the sidebar's own RBAC.
  const searchResults = searchMatches.filter((r) => r.roleChain.every(roleOk))

  return (
    /* Hover-to-expand is bound to the whole drawer surface, not just the nav area below.
       Scoped to the nav area, moving the pointer up to the search field left the hover zone,
       collapsed the rail and unmounted the field before it could be typed in. */
    <Drawer
      variant='persistent'
      anchor='left'
      open={true}
      PaperProps={{
        sx: sx.drawer(drawerWidth),
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
      }}
    >
      {/* ── Logo + toggle (dead zone — no hover effect) ── */}
      <Box sx={sx.logoArea}>
        <Box sx={sx.logoImg(isExpanded)}>
          <img src='/img/pl.png' alt='Logo' style={{height: 50}} />
        </Box>
        <IconButton size='small' onClick={() => setSidebarWidth(!sidebarWidth)} sx={sx.toggleBtn}>
          {sidebarWidth && !isHovered ? (
            <KeyboardDoubleArrowRightIcon fontSize='small' />
          ) : (
            <KeyboardDoubleArrowLeftIcon fontSize='small' />
          )}
        </IconButton>
      </Box>

      <Divider sx={sx.divider} />

      {/* ── Search + Expand All ── */}
      {isExpanded && (
        <Box sx={{px: 1.5, pt: 1.5, pb: 0.5}}>
          <Box sx={{display: query ? 'none' : 'flex', gap: 0.5, mb: 1}}>
            <Tooltip title={allExpanded ? 'Collapse All' : 'Expand All'} placement='right' arrow>
              <IconButton
                size='small'
                onClick={() => {
                  if (allExpanded) {
                    setOpenSubMenu('')
                    setOpenNestedSubMenu('')
                    setAllExpanded(false)
                  } else {
                    setAllExpanded(true)
                  }
                }}
                sx={{
                  color: '#919EAB', borderRadius: '8px', border: '1px solid rgba(145,158,171,0.2)',
                  width: '100%', fontSize: '11px', gap: 0.5,
                  '&:hover': {backgroundColor: 'rgba(145,158,171,0.08)', color: '#2065D1'},
                }}
              >
                {allExpanded ? <UnfoldLessIcon sx={{fontSize: 16}} /> : <UnfoldMoreIcon sx={{fontSize: 16}} />}
                <Typography sx={{fontSize: '11px', fontWeight: 600}}>
                  {allExpanded ? 'Collapse All' : 'Expand All'}
                </Typography>
              </IconButton>
            </Tooltip>
          </Box>
          <TextField
            size='small'
            placeholder='Search menu...'
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => e.key === 'Escape' && setSearchText('')}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon sx={{fontSize: 18, color: '#637381'}} />
                </InputAdornment>
              ),
              endAdornment: searchText ? (
                <InputAdornment position='end'>
                  <IconButton size='small' onClick={() => setSearchText('')} sx={{color: '#637381', p: 0.3}}>
                    <ClearIcon sx={{fontSize: 16}} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(145,158,171,0.08)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '12px',
                height: 36,
                '& fieldset': {borderColor: 'rgba(145,158,171,0.2)'},
                '&:hover fieldset': {borderColor: 'rgba(145,158,171,0.4)'},
                '&.Mui-focused fieldset': {borderColor: '#2065D1'},
              },
              '& .MuiInputBase-input::placeholder': {color: '#637381', opacity: 1},
            }}
          />
        </Box>
      )}

      {/* ── Navigation ── */}
      <Box sx={sx.navArea}>
        {/* ── Search results — a flat list replaces the tree while a query is active ── */}
        {query && (
          <List disablePadding>
            <Box sx={{...sx.sectionHeader(isExpanded), display: 'flex', alignItems: 'center', gap: 1}}>
              {searchResults.length > 0
                ? `${searchResults.length} RESULT${searchResults.length > 1 ? 'S' : ''}`
                : 'NO RESULTS'}
            </Box>
            {searchResults.length === 0 ? (
              <Typography sx={{px: 2, py: 1.5, color: '#637381', fontSize: '12px', lineHeight: 1.6}}>
                Nothing matches “{query}”. Try a shorter word, or clear the search to browse the
                menu.
              </Typography>
            ) : (
              searchResults.map((r) => (
                <ListItemButton
                  key={`${r.name}|${r.url}`}
                  component={Link}
                  to={r.url}
                  onClick={() => setSearchText('')}
                  sx={sx.resultItem(isItemActive(r.url))}
                >
                  <ListItemIcon sx={sx.resultIcon}>
                    {r.icon || <FiberManualRecordIcon sx={{fontSize: 6}} />}
                  </ListItemIcon>
                  <ListItemText
                    primary={highlightMatch(r.name)}
                    secondary={highlightMatch(r.breadcrumb)}
                    primaryTypographyProps={{fontSize: '12px', fontWeight: 500, noWrap: true}}
                    secondaryTypographyProps={{fontSize: '10px', color: '#637381', noWrap: true}}
                  />
                  {r.rbacPersona && (
                    <Tooltip
                      title={RBAC_PERSONA_TOOLTIP[r.rbacPersona] || r.rbacPersona}
                      placement='right'
                      arrow
                    >
                      <Box sx={sx.resultPersona(r.rbacPersona)}>{r.rbacPersona}</Box>
                    </Tooltip>
                  )}
                  {r.badgeKey && kycCounts[r.badgeKey] > 0 && (
                    <Box sx={sx.resultBadge(r.badgeBg, r.badgeColor)}>{kycCounts[r.badgeKey]}</Box>
                  )}
                </ListItemButton>
              ))
            )}
          </List>
        )}

        <List disablePadding sx={{display: query ? 'none' : 'block'}}>
          {MENU_ITEMS.map((menu, i) => {
            // ── Section headers ──
            if (menu.type === 'section') {
              // Suppress the header when no item beneath it (up to the next section) is visible.
              let sectionHasVisible = false
              for (let k = i + 1; k < MENU_ITEMS.length; k++) {
                if (MENU_ITEMS[k].type === 'section') break
                if (isItemVisible(MENU_ITEMS[k])) {
                  sectionHasVisible = true
                  break
                }
              }
              if (!sectionHasVisible) return null
              return (
                <Box key={`s-${i}`} sx={{...sx.sectionHeader(isExpanded), display: 'flex', alignItems: 'center', gap: 1}}>
                  {isExpanded && SECTION_ICONS[menu.name] && <Box sx={{display: 'flex', opacity: 0.7}}>{SECTION_ICONS[menu.name]}</Box>}
                  {isExpanded ? menu.name : ''}
                </Box>
              )
            }

            // ── Access check (role + at least one visible child for groups) ──
            if (!isItemVisible(menu)) return null

            const isActive =
              loc.pathname === menu.url ||
              (menu.submenu && menu.submenu.some((s) => {
                if (!s || s.type) return false
                if (s.url?.includes('?')) return fullLoc === s.url
                return matchesPath(loc.pathname, s.url)
              }))
            const highlighted = isActive || selectedMenu?.name === menu?.name
            const isSubMenuOpen = allExpanded ? true : openSubMenu === menu.name
            const hasSubmenu = menu.submenu && menu.submenu.length > 0

            const item = (
              <ListItemButton
                onClick={() => hasSubmenu ? handleSubMenuClick(menu.name) : null}
                component={hasSubmenu ? 'div' : Link}
                to={hasSubmenu ? undefined : menu.url}
                sx={sx.menuItem(highlighted, isExpanded)}
              >
                <ListItemIcon sx={sx.menuIcon(highlighted, isExpanded)}>
                  {menu.icon}
                </ListItemIcon>
                {isExpanded && (
                  <>
                    <ListItemText
                      primary={highlightMatch(menu.name)}
                      primaryTypographyProps={{fontSize: '13px', fontWeight: highlighted ? 600 : 400, noWrap: true}}
                    />
                    {menu.tag && (
                      <Box sx={{
                        fontSize: '9px', fontWeight: 800, px: 0.7, py: 0.15, borderRadius: '4px',
                        background: 'rgba(234,164,37,0.22)', color: '#f59e0b',
                        letterSpacing: '0.3px', flexShrink: 0, mr: hasSubmenu ? 0.5 : 0,
                        border: '1px solid rgba(234,164,37,0.3)',
                      }}>
                        {menu.tag}
                      </Box>
                    )}
                    {hasSubmenu && (
                      <Box sx={sx.expandIcon}>
                        {isSubMenuOpen ? <ExpandLess sx={{fontSize: 18}} /> : <ExpandMore sx={{fontSize: 18}} />}
                      </Box>
                    )}
                  </>
                )}
              </ListItemButton>
            )

            return (
              <Box key={i}>
                {!isExpanded ? (
                  <Tooltip title={menu.name} placement='right' arrow>
                    {item}
                  </Tooltip>
                ) : (
                  item
                )}

                {/* ── Sub-menu ── */}
                {hasSubmenu && (
                  <Collapse in={isExpanded && isSubMenuOpen} timeout={200} unmountOnExit>
                    <List disablePadding sx={sx.subMenuContainer}>
                      {menu.submenu.map((sub, j) => {
                        if (sub.type === 'divider') {
                          return (
                            <Typography key={`d-${j}`} sx={sx.subDivider}>
                              {sub.name}
                            </Typography>
                          )
                        }

                        if (!isItemVisible(sub)) return null

                        const subActive = isItemActive(sub.url)
                        const hasNested = sub.submenu && sub.submenu.length > 0
                        const isNestedOpen = allExpanded ? true : openNestedSubMenu === sub.url
                        const isDisabled = !!sub.disabled

                        return (
                          <Box key={j}>
                            <ListItemButton
                              onClick={() => (isDisabled ? null : hasNested ? handleNestedSubMenuClick(sub.url) : null)}
                              component={isDisabled || hasNested ? 'div' : Link}
                              to={isDisabled || hasNested ? undefined : sub.url}
                              disabled={isDisabled}
                              sx={sx.subMenuItem(subActive, isDisabled)}
                            >
                              <FiberManualRecordIcon sx={sx.dot(subActive)} />
                              <ListItemText
                                primary={highlightMatch(sub.name)}
                                primaryTypographyProps={{fontSize: '12px', fontWeight: subActive ? 600 : 400, noWrap: true}}
                              />
                              {sub.tag && (
                                <Box sx={sx.subTag}>
                                  {sub.tag}
                                </Box>
                              )}
                              {sub.rbacPersona && (
                                <Tooltip
                                  title={RBAC_PERSONA_TOOLTIP[sub.rbacPersona] || sub.rbacPersona}
                                  placement='right'
                                  arrow
                                >
                                  <Box
                                    sx={{
                                      ml: 'auto',
                                      px: 0.6,
                                      borderRadius: '4px',
                                      fontSize: '9px',
                                      fontWeight: 700,
                                      letterSpacing: '0.4px',
                                      lineHeight: '16px',
                                      backgroundColor: RBAC_PERSONA_BG[sub.rbacPersona] || 'rgba(145,158,171,0.16)',
                                      color: RBAC_PERSONA_FG[sub.rbacPersona] || '#919EAB',
                                      minWidth: 26,
                                      textAlign: 'center',
                                      flexShrink: 0,
                                      border: '1px solid rgba(255,255,255,0.06)',
                                    }}
                                  >
                                    {sub.rbacPersona}
                                  </Box>
                                </Tooltip>
                              )}
                              {sub.badgeKey && kycCounts[sub.badgeKey] > 0 && (
                                <Box
                                  sx={{
                                    ml: sub.rbacPersona ? 0.5 : 'auto',
                                    px: 0.8,
                                    borderRadius: '4px',
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    lineHeight: '18px',
                                    backgroundColor: sub.badgeBg || 'rgba(99,102,241,0.14)',
                                    color: sub.badgeColor || '#6366f1',
                                    minWidth: 20,
                                    textAlign: 'center',
                                    flexShrink: 0,
                                  }}
                                >
                                  {kycCounts[sub.badgeKey]}
                                </Box>
                              )}
                              {hasNested && (
                                <Box sx={sx.expandIcon}>
                                  {isNestedOpen ? <ExpandLess sx={{fontSize: 16}} /> : <ExpandMore sx={{fontSize: 16}} />}
                                </Box>
                              )}
                            </ListItemButton>

                            {/* ── Nested sub-menu (3-level) ── */}
                            {hasNested && (
                              <Collapse in={isNestedOpen} timeout={200} unmountOnExit>
                                <List disablePadding sx={sx.nestedContainer}>
                                  {sub.submenu.map((nested, k) => {
                                    if (!isItemVisible(nested)) return null
                                    return (
                                    <ListItemButton
                                      key={k}
                                      component={Link}
                                      to={nested.url}
                                      sx={sx.subMenuItem(isItemActive(nested.url))}
                                    >
                                      <FiberManualRecordIcon sx={sx.dot(isItemActive(nested.url))} />
                                      <ListItemText
                                        primary={highlightMatch(nested.name)}
                                        primaryTypographyProps={{fontSize: '11px', noWrap: true}}
                                      />
                                    </ListItemButton>
                                    )
                                  })}
                                </List>
                              </Collapse>
                            )}
                          </Box>
                        )
                      })}
                    </List>
                  </Collapse>
                )}
              </Box>
            )
          })}
        </List>
      </Box>
    </Drawer>
  )
}

// ── All styles in one object — easy to scan ──
const C = {
  bg: '#161C24',
  text: '#919EAB',
  textActive: '#fff',
  hover: 'rgba(145,158,171,0.08)',
  hoverText: '#2065D1',
  accent: '#2065D1',
  sectionText: '#637381',
  line: 'rgba(145,158,171,0.15)',
  divider: 'rgba(145,158,171,0.12)',
}

const TRANSITION = 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)'

const sx = {
  drawer: (width) => ({
    width,
    backgroundColor: C.bg,
    color: C.text,
    borderRight: 'none',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    transition: TRANSITION,
    '&::-webkit-scrollbar': {width: 3},
    '&::-webkit-scrollbar-thumb': {background: C.line, borderRadius: 4},
    scrollbarWidth: 'thin',
    scrollbarColor: `${C.line} transparent`,
  }),

  // ── Logo area (no hover effect) ──
  logoArea: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: 1.5,
    py: 1.5,
    minHeight: 56,
  },
  logoImg: (show) => ({
    opacity: show ? 1 : 0,
    transition: 'opacity 0.2s',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  }),
  toggleBtn: {
    color: C.text,
    '&:hover': {color: C.hoverText, backgroundColor: C.hover},
  },
  divider: {borderColor: C.divider, mx: 1},

  // ── Navigation area ──
  navArea: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    py: 0.5,
  },

  // ── Section header — visually prominent band for D2C / PTL / ADMIN ──
  sectionHeader: (isExpanded) => ({
    px: isExpanded ? 2 : 1,
    pt: isExpanded ? 1.5 : 1,
    pb: isExpanded ? 1 : 0.5,
    mt: 0.5,
    mx: 1,
    mb: 0.5,
    fontSize: '10px',
    fontWeight: 800,
    letterSpacing: '1.2px',
    textTransform: 'uppercase',
    color: '#fff',
    minHeight: isExpanded ? 'auto' : 8,
    transition: 'padding 0.25s, min-height 0.25s',
    borderRadius: '6px',
    background: 'linear-gradient(135deg, rgba(32,101,209,0.18) 0%, rgba(32,101,209,0.06) 100%)',
    borderLeft: `3px solid ${C.accent}`,
  }),

  // ── Menu item ──
  menuItem: (isActive, isExpanded) => ({
    mx: 1,
    mb: 0.3,
    borderRadius: '8px',
    px: isExpanded ? 1.5 : 0,
    justifyContent: isExpanded ? 'flex-start' : 'center',
    minHeight: 42,
    color: isActive ? C.textActive : C.text,
    borderLeft: isActive ? `3px solid ${C.accent}` : '3px solid transparent',
    transition: 'padding 0.25s, justify-content 0.25s',
    '&:hover': {backgroundColor: C.hover, color: C.hoverText},
    ...(isActive && {backgroundColor: `rgba(32,101,209,0.12)`}),
  }),

  menuIcon: (isActive, isExpanded) => ({
    color: isActive ? C.textActive : C.text,
    minWidth: isExpanded ? 36 : 'unset',
    mr: isExpanded ? 1 : 0,
    justifyContent: 'center',
    transition: 'min-width 0.25s, margin 0.25s',
    '& svg': {fontSize: 20},
  }),

  expandIcon: {
    display: 'flex',
    alignItems: 'center',
    color: C.text,
    ml: 'auto',
  },

  // ── Search match highlight ──
  highlight: {
    color: '#2065D1',
    backgroundColor: 'rgba(32,101,209,0.18)',
    borderRadius: '3px',
    fontWeight: 700,
  },

  // ── Sub-menu ──
  subMenuContainer: {
    ml: '28px',
    pl: 2,
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: '1px',
      backgroundColor: C.line,
    },
  },

  subMenuItem: (isActive, isDisabled) => ({
    py: 0.4,
    px: 1,
    borderRadius: '6px',
    color: isActive ? C.textActive : C.text,
    ...(isDisabled
      ? {opacity: 0.5, cursor: 'default', '&:hover': {color: C.text, backgroundColor: 'transparent'}}
      : {'&:hover': {color: C.hoverText}}),
  }),

  subTag: {
    fontSize: '8px',
    fontWeight: 800,
    px: 0.6,
    py: 0.1,
    borderRadius: '4px',
    background: 'rgba(234,164,37,0.18)',
    color: '#f59e0b',
    letterSpacing: '0.3px',
    flexShrink: 0,
    ml: 'auto',
    border: '1px solid rgba(234,164,37,0.28)',
  },

  // ── Search result row — flat, so it carries its own icon and a breadcrumb ──
  resultItem: (isActive) => ({
    mx: 1,
    mb: 0.3,
    px: 1.5,
    borderRadius: '8px',
    minHeight: 42,
    color: isActive ? C.textActive : C.text,
    borderLeft: isActive ? `3px solid ${C.accent}` : '3px solid transparent',
    ...(isActive && {backgroundColor: 'rgba(32,101,209,0.12)'}),
    '&:hover': {backgroundColor: C.hover, color: C.hoverText},
  }),

  resultIcon: {
    color: 'inherit',
    minWidth: 32,
    justifyContent: 'center',
    '& svg': {fontSize: 18},
  },

  resultPersona: (persona) => ({
    ml: 'auto',
    px: 0.6,
    borderRadius: '4px',
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.4px',
    lineHeight: '16px',
    backgroundColor: RBAC_PERSONA_BG[persona] || 'rgba(145,158,171,0.16)',
    color: RBAC_PERSONA_FG[persona] || '#919EAB',
    minWidth: 26,
    textAlign: 'center',
    flexShrink: 0,
    border: '1px solid rgba(255,255,255,0.06)',
  }),

  resultBadge: (bg, color) => ({
    ml: 0.5,
    px: 0.8,
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 700,
    lineHeight: '18px',
    backgroundColor: bg || 'rgba(99,102,241,0.14)',
    color: color || '#6366f1',
    minWidth: 20,
    textAlign: 'center',
    flexShrink: 0,
  }),

  dot: (isActive) => ({
    fontSize: 5,
    color: isActive ? C.textActive : C.text,
    mr: 1.5,
    flexShrink: 0,
  }),

  subDivider: {
    px: 1,
    pt: 1.5,
    pb: 0.5,
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: C.sectionText,
    cursor: 'default',
  },

  // ── Nested sub-menu ──
  nestedContainer: {
    ml: 2,
    pl: 2,
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: '1px',
      backgroundColor: C.line,
    },
  },
}

export default SidebarNew
